import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Calendar as CalendarIcon, ArrowRight, MapPin, Clock } from "lucide-react";
import {
  WO_TIPO_DOT, WO_ESTADO_LABEL, WO_ESTADO_STYLE, formatFechaCorta,
} from "@/lib/workOrders";

// Home widget: shows the next 5 work orders assigned to the current user.
export default function UpcomingOrdersWidget() {
  const [orders, setOrders] = useState(null); // null=loading, []=empty

  useEffect(() => {
    api.get("/work-orders/mine/upcoming", { params: { limit: 5 } })
      .then((r) => setOrders(r.data || []))
      .catch(() => setOrders([]));
  }, []);

  return (
    <section className="brutal-card p-4 space-y-3" data-testid="upcoming-orders-widget">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0B2A4A] to-[#0E78C2] text-white flex items-center justify-center">
            <CalendarIcon size={18} />
          </div>
          <div>
            <div className="label-up text-slate-500">Mi agenda</div>
            <div className="font-display font-black text-lg text-[#0B2A4A] leading-tight">
              Próximas órdenes
            </div>
          </div>
        </div>
        <Link
          to="/orders"
          data-testid="upcoming-orders-see-all"
          className="text-xs font-semibold uppercase tracking-wider text-[#0E78C2] hover:underline inline-flex items-center gap-1"
        >
          Ver calendario <ArrowRight size={12} />
        </Link>
      </div>

      {orders === null ? (
        <div className="text-xs text-slate-500 py-2">Cargando…</div>
      ) : orders.length === 0 ? (
        <div className="text-xs text-slate-500 py-2">
          No tienes órdenes asignadas próximamente.
        </div>
      ) : (
        <ul className="space-y-2">
          {orders.map((o) => (
            <li key={o.id}>
              <Link
                to="/orders"
                state={{ orderId: o.id }}
                data-testid={`upcoming-order-${o.id}`}
                className="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <span
                  className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                  style={{ background: WO_TIPO_DOT[o.tipo] || "#94a3b8" }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${WO_ESTADO_STYLE[o.estado]}`}>
                      {WO_ESTADO_LABEL[o.estado]}
                    </span>
                    <span className="text-[10px] font-mono text-slate-400">
                      {formatFechaCorta(o.fecha)}
                      {o.fecha_fin && o.fecha_fin !== o.fecha
                        ? ` → ${formatFechaCorta(o.fecha_fin)}`
                        : o.todo_el_dia
                          ? " · Día completo"
                          : o.hora_inicio ? ` · ${o.hora_inicio}` : ""}
                    </span>
                  </div>
                  <div className="font-semibold text-sm text-[#0B2A4A] leading-tight truncate mt-0.5">
                    {o.titulo}
                  </div>
                  {o.direccion && (
                    <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-0.5 truncate">
                      <MapPin size={10} /> {o.direccion}
                    </div>
                  )}
                </div>
                <ArrowRight size={14} className="text-slate-400 mt-1" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
